import { apiSlice } from "./apiSlice";
import {
  PaginatedProjectsResponse,
  ProjectEntity,
} from "../../services/projects/projects.types";

export interface GetProjectsPaginatedArgs {
  page: number;
  pageSize: number;
  id?: number;
  title?: string;
  program_id?: number;
}

export interface ProjectsPage {
  data: ProjectEntity[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
}

export const projectsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProjectsPaginated: builder.query<ProjectsPage, GetProjectsPaginatedArgs>(
      {
        query: ({ page, pageSize, id, title, program_id }) => ({
          url: "/projects",
          method: "GET",
          params: {
            page,
            // The projects backend takes the page size as `limit` but answers
            // with `pageSize`; the app-facing arg is always `pageSize`.
            limit: pageSize,
            ...(id !== undefined && { id }),
            ...(title && { title }),
            ...(program_id !== undefined && { program_id }),
          },
        }),
        transformResponse: (
          response: PaginatedProjectsResponse,
        ): ProjectsPage => ({
          data: response.data,
          pagination: {
            page: response.pagination.page,
            pageSize: response.pagination.pageSize,
            total: response.pagination.total,
          },
        }),
        providesTags: (result) =>
          result
            ? [
                ...result.data.map(({ id }) => ({
                  type: "Project" as const,
                  id,
                })),
                { type: "Project" as const, id: "LIST" },
              ]
            : [{ type: "Project" as const, id: "LIST" }],
      },
    ),
    deleteProject: builder.mutation<void, number>({
      query: (id) => ({
        url: `/projects/${id}`,
        method: "DELETE",
      }),
      // Deleted projects land in the trash, so its cache must refresh too.
      // Error guard: a failed delete must not refetch anything.
      invalidatesTags: (result, error) =>
        error
          ? []
          : [{ type: "Project" as const, id: "LIST" }, "Trash" as const],
    }),
  }),
});

export const { useGetProjectsPaginatedQuery, useDeleteProjectMutation } =
  projectsApi;
